import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { send } from '@/shared/messages';
import type { ChatMessage, ChatThread } from '@/shared/types';
import { ACTIVE_CHAT_KEY } from '@/shared/constants';
import { GitFork } from 'lucide-react';
import { ChatTranscript } from '../components/ChatTranscript';
import { ChatComposer } from '../components/ChatComposer';
import { ChatList } from '../components/ChatList';
import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';

// Chat is the cross-content surface (not scoped to the active test). Multi-chat:
// a collapsible list of previous threads + New, auto-titled from the first
// message. The assistant decides what chapter context to load via tools.
//
// The URL (/chat/:chatId) is the source of truth for the open thread, so deep
// links and browser history work; bare /chat resolves to the last-active (or
// most-recent, or a freshly created) thread and redirects to its canonical URL.
export function ChatRoute({ onOpenChapter }: { onOpenChapter?: (chapterId: string) => void }) {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const activeChatId = chatId ?? null;
  const [chats, setChats] = useState<ChatThread[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load the thread list once. With no :chatId in the URL, redirect to the
  // resolved thread's canonical URL so the address bar always names it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let list = (await send({ type: 'LIST_CHATS' })).chats;
      if (cancelled) return;
      if (list.length === 0) {
        const created = (await send({ type: 'CREATE_CHAT' })).chat;
        if (cancelled) return;
        list = [created];
      }
      setChats(list);
      if (!chatId) {
        const stored = await readActiveChatId();
        const target = stored && list.some((c) => c.id === stored) ? stored : list[0].id;
        if (!cancelled) navigate(`/chat/${target}`, { replace: true });
      }
    })().catch((e) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : String(e));
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
    // Mount-only: subsequent thread switches flow through the URL, handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the active thread's messages whenever the URL names a different one.
  useEffect(() => {
    if (!activeChatId) return;
    let cancelled = false;
    setStreamingText('');
    void persistActiveChatId(activeChatId);
    (async () => {
      try {
        const msgs = (await send({ type: 'CHAT_LIST_MESSAGES', chatId: activeChatId })).messages;
        if (cancelled) return;
        setMessages(msgs);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeChatId]);

  // Stream deltas for the active chat only (answer text + Searching/Loading lines).
  useEffect(() => {
    function onMessage(m: unknown) {
      if (!m || typeof m !== 'object' || !('type' in m)) return;
      if ((m as { type: string }).type !== 'LLM_DELTA') return;
      const d = m as unknown as { surface: 'regen' | 'qa' | 'chat'; chatId?: string; text: string };
      if (d.surface !== 'chat' || d.chatId !== activeChatId) return;
      setStreamingText(d.text);
    }
    chrome.runtime.onMessage.addListener(onMessage);
    return () => chrome.runtime.onMessage.removeListener(onMessage);
  }, [activeChatId]);

  // Thread switches go through the URL; the messages effect above reloads.
  function selectChat(id: string) {
    setListOpen(false);
    if (id === activeChatId) return;
    setError(null);
    navigate(`/chat/${id}`);
  }

  async function newChat() {
    setError(null);
    try {
      const chat = (await send({ type: 'CREATE_CHAT' })).chat;
      setChats((cs) => [chat, ...cs]);
      setMessages([]);
      setStreamingText('');
      setListOpen(false);
      navigate(`/chat/${chat.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // Fork = duplicate the thread (messages + pinned context) into a new chat
  // and jump to it, leaving the original untouched.
  async function forkChat(id: string) {
    setError(null);
    try {
      const fork = (await send({ type: 'FORK_CHAT', chatId: id })).chat;
      setChats((cs) => [fork, ...cs]);
      setListOpen(false);
      navigate(`/chat/${fork.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function renameChat(id: string, title: string) {
    setChats((cs) => cs.map((c) => (c.id === id ? { ...c, title } : c)));
    try {
      await send({ type: 'RENAME_CHAT', chatId: id, title });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function deleteChat(id: string) {
    setError(null);
    try {
      await send({ type: 'DELETE_CHAT', chatId: id });
      const remaining = chats.filter((c) => c.id !== id);
      setChats(remaining);
      if (id === activeChatId) {
        if (remaining.length > 0) {
          navigate(`/chat/${remaining[0].id}`);
        } else {
          await newChat();
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function ask() {
    const question = input.trim();
    if (!question || pending || !activeChatId) return;
    const chatId = activeChatId;
    setError(null);
    const optimistic: ChatMessage = {
      id: crypto.randomUUID(),
      chat_id: chatId,
      role: 'user',
      content: question,
      created_at: Date.now(),
    };
    setMessages((m) => [...m, optimistic]);
    setPending(true);
    setStreamingText('');
    try {
      const r = await send({ type: 'CHAT_ASK', chatId, question });
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          chat_id: chatId,
          role: 'assistant',
          content: r.reply,
          created_at: Date.now(),
        },
      ]);
      setInput('');
      // Refresh the list so the auto-title + recency reorder show up.
      void send({ type: 'LIST_CHATS' }).then((res) => setChats(res.chats));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMessages((m) => m.filter((msg) => msg.id !== optimistic.id));
    } finally {
      setPending(false);
      setStreamingText('');
    }
  }

  if (!loaded) {
    return <div className="p-4 text-sm text-slate-500">Loading chat…</div>;
  }

  const activeTitle = chats.find((c) => c.id === activeChatId)?.title || 'New chat';

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-card px-3 py-1.5">
        <button
          onClick={() => setListOpen((o) => !o)}
          className={`rounded px-2 py-1 text-xs ${
            listOpen ? 'bg-slate-200 font-medium' : 'text-slate-600 hover:bg-slate-100'
          }`}
          title="Previous chats"
        >
          ☰ Chats
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
          {listOpen ? 'Previous chats' : activeTitle}
        </span>
        {activeChatId && !listOpen && (
          <IconButton
            label="Fork this chat"
            size="sm"
            icon={<GitFork size={14} strokeWidth={1.75} />}
            disabled={pending}
            onClick={() => void forkChat(activeChatId)}
          />
        )}
        <Button variant="primary" size="xs" className="shrink-0" onClick={() => void newChat()}>
          + New
        </Button>
      </div>

      {error && (
        <div className="border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {listOpen ? (
        <ChatList
          chats={chats}
          activeChatId={activeChatId}
          onSelect={(id) => void selectChat(id)}
          onRename={(id, t) => void renameChat(id, t)}
          onDelete={(id) => void deleteChat(id)}
          onFork={(id) => void forkChat(id)}
        />
      ) : (
        <>
          <ChatTranscript
            messages={messages}
            pending={pending}
            streamingText={streamingText}
            onChapterLink={onOpenChapter}
          />
          <ChatComposer value={input} onChange={setInput} pending={pending} onSend={ask} />
        </>
      )}
    </div>
  );
}

async function readActiveChatId(): Promise<string | null> {
  try {
    const r = await chrome.storage.local.get(ACTIVE_CHAT_KEY);
    return typeof r[ACTIVE_CHAT_KEY] === 'string' ? r[ACTIVE_CHAT_KEY] : null;
  } catch {
    return null;
  }
}

async function persistActiveChatId(id: string): Promise<void> {
  try {
    await chrome.storage.local.set({ [ACTIVE_CHAT_KEY]: id });
  } catch {
    /* non-extension context without storage — ignore */
  }
}
